import type { GbpDate, GbpTime, GbpEvent } from '../../types/publishing.js';

// Publishing — Google Business Profile date/time helpers. GBP's event schedule
// uses Google's native {year,month,day} + {hours,minutes} OBJECT format, NOT ISO
// (confirmed from GoogleBusinessPlatformData in the SDK). These convert between an
// ISO datetime (what the composer's <input type="datetime-local"> produces, in the
// browser's local zone) and the object parts. Pure + total.
//
// NOTE: we split on the LOCAL parts of the Date so the AM sees the same wall-clock
// time they typed. Google interprets the parts in the business's own timezone.

export function isoToGbpDate(iso: string): GbpDate | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
}

export function isoToGbpTime(iso: string): GbpTime | null {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return { hours: d.getHours(), minutes: d.getMinutes() };
}

// Build a GbpEvent from a title + start/end ISO datetimes. Returns null when a
// required field is missing/unparseable so the caller can surface a validation
// error rather than send a malformed event object.
export function buildGbpEvent(input: {
  title: string;
  startIso: string;
  endIso: string;
  includeTimes?: boolean;
}): GbpEvent | null {
  const title = input.title.trim();
  const startDate = isoToGbpDate(input.startIso);
  const endDate = isoToGbpDate(input.endIso);
  if (title === '' || !startDate || !endDate) return null;
  const event: GbpEvent = { title, startDate, endDate };
  if (input.includeTimes) {
    event.startTime = isoToGbpTime(input.startIso);
    event.endTime = isoToGbpTime(input.endIso);
  }
  return event;
}

// Serialize a GbpDate/GbpTime back to a datetime-local input value
// ("YYYY-MM-DDTHH:mm") so the composer can round-trip a stored event.
export function gbpEventToStartInput(event: GbpEvent): string {
  return gbpPartsToInput(event.startDate, event.startTime ?? null);
}
export function gbpEventToEndInput(event: GbpEvent): string {
  return gbpPartsToInput(event.endDate, event.endTime ?? null);
}

function gbpPartsToInput(date: GbpDate, time: GbpTime | null): string {
  const p2 = (n: number) => String(n).padStart(2, '0');
  const hh = p2(time?.hours ?? 0);
  const mm = p2(time?.minutes ?? 0);
  return `${date.year}-${p2(date.month)}-${p2(date.day)}T${hh}:${mm}`;
}
